import { cn } from "@/lib/utils";

/**
 * Iconografía del Design System — Material Symbols Rounded.
 *
 * Este módulo reemplaza a `lucide-react`. Exporta un componente por ícono con
 * el MISMO nombre que tenía en Lucide, así los ~390 usos de la app siguen
 * escribiéndose igual (`<Plus className="h-4 w-4" />`) y sólo cambia el import.
 * Varios íconos además viajan como valor dentro de objetos de configuración
 * (el menú del sidebar, el CTA de `PageHeader`), por eso tienen que ser
 * componentes y no un string.
 *
 * TAMAÑO: Material Symbols es una FUENTE — el glifo escala por `font-size`, no
 * por `width`/`height`. Las utilidades de tamaño que ya usaba la app (`h-4`,
 * `size-4`, …) se traducen a `font-size` en `app.css`, sección "tamaño del
 * glifo". Si necesitás un tamaño exacto fuera de esa escala, usá `<Icon>` con
 * su prop `size`.
 *
 * Para un ícono nuevo NO agregues otra librería: buscá el glifo en
 * https://fonts.google.com/icons (family: Material Symbols Rounded) y sumalo
 * acá abajo.
 */

export type FcIconProps = React.ComponentProps<"span">;

/** Forma de un ícono del sistema. Reemplaza al tipo `LucideIcon`. */
export type FcIcon = (props: FcIconProps) => React.ReactElement;

function glyph(name: string): FcIcon {
  function GlyphIcon({ className, ...props }: FcIconProps) {
    return (
      <span
        aria-hidden="true"
        data-icon={name}
        className={cn("material-symbols-rounded", className)}
        {...props}
      >
        {name}
      </span>
    );
  }
  GlyphIcon.displayName = `Icon(${name})`;
  return GlyphIcon;
}

/* --- acciones ----------------------------------------------------------- */
export const Plus = glyph("add");
export const Pencil = glyph("edit");
/** "Hay ediciones sin guardar" — el aviso, no la acción de editar. */
export const EditNote = glyph("edit_note");
export const Save = glyph("save");
export const Trash = glyph("delete");
export const Trash2 = glyph("delete");
export const ArchiveRestore = glyph("unarchive");
export const Download = glyph("download");
export const Upload = glyph("upload");
export const Search = glyph("search");
export const SearchIcon = glyph("search");
export const RefreshCw = glyph("refresh");
/** Reemplazar un archivo ya cargado. Glifo distinto a `refresh` (recargar):
 * el diseño usa `autorenew` para "sube otro en lugar de este". */
export const Autorenew = glyph("autorenew");
export const Send = glyph("send");
export const Settings = glyph("settings");
export const Share2 = glyph("share");
export const Link2 = glyph("link");
export const Unlink = glyph("link_off");
export const Eye = glyph("visibility");
export const EyeOff = glyph("visibility_off");
export const PlayCircle = glyph("play_circle");
export const MessageSquarePlus = glyph("add_comment");

/* --- navegación y dirección --------------------------------------------- */
export const ArrowLeft = glyph("arrow_back");
export const ArrowRight = glyph("arrow_forward");
export const ArrowUpRight = glyph("north_east");
export const ArrowDownRight = glyph("south_east");
export const ChevronLeft = glyph("chevron_left");
export const ChevronLeftIcon = glyph("chevron_left");
export const ChevronRight = glyph("chevron_right");
export const ChevronRightIcon = glyph("chevron_right");
export const ChevronDown = glyph("expand_more");
export const ChevronDownIcon = glyph("expand_more");
export const ChevronUp = glyph("expand_less");
export const ChevronUpIcon = glyph("expand_less");
export const ChevronsUpDown = glyph("unfold_more");
export const MoreHorizontal = glyph("more_horiz");
export const MoreHorizontalIcon = glyph("more_horiz");
export const PanelLeftIcon = glyph("left_panel_open");
export const GripVerticalIcon = glyph("drag_indicator");
export const Compass = glyph("explore");

/* --- estado y feedback --------------------------------------------------- */
export const Check = glyph("check");
export const CheckIcon = glyph("check");
export const CheckCircle = glyph("check_circle");
export const CheckCircle2 = glyph("check_circle");
export const X = glyph("close");
export const XIcon = glyph("close");
export const XCircle = glyph("cancel");
export const AlertCircle = glyph("error");
export const Info = glyph("info");
export const HelpCircle = glyph("help");
export const Minus = glyph("remove");
export const MinusIcon = glyph("remove");
export const Circle = glyph("circle");
export const CircleIcon = glyph("circle");
/** Spinner del DS: va siempre con `animate-fc-spin` (o `animate-spin`). */
export const Loader2 = glyph("progress_activity");
export const Loader2Icon = glyph("progress_activity");
export const Sparkles = glyph("auto_awesome");
export const Zap = glyph("bolt");
export const TrendingUp = glyph("trending_up");
export const TrendingDown = glyph("trending_down");
export const Ban = glyph("block");
export const Undo2 = glyph("undo");
export const Hourglass = glyph("pending");
export const Clock = glyph("schedule");

/* --- personas y accesos --------------------------------------------------- */
export const User = glyph("person");
export const Users = glyph("group");
export const UserPlus = glyph("person_add");
export const UserCheck = glyph("how_to_reg");
export const UserCog = glyph("manage_accounts");
export const IdCard = glyph("badge");
/** La persona identificada por su RUT — distinto de `IdCard`, que es la
 * credencial/nombre con que se la conoce. Lo usa el registro para separar
 * "nombre de fantasía" de "tu RUT". */
export const Fingerprint = glyph("fingerprint");
export const Lock = glyph("lock");
/** Restablecer la contraseña, no cerrar con llave: el candado con la flecha. */
export const LockReset = glyph("lock_reset");
export const KeyRound = glyph("key");
export const LogOut = glyph("logout");
export const Shield = glyph("shield");
export const HardHat = glyph("engineering");

/* --- negocio y documentos -------------------------------------------------- */
export const FileText = glyph("description");
/** Documento firmado en terreno — el buzón de registros. */
export const Signature = glyph("draw");
export const FileCheck2 = glyph("fact_check");
export const ScrollText = glyph("article");
export const Inbox = glyph("inbox");
export const GraduationCap = glyph("school");
export const Briefcase = glyph("work");
export const Building = glyph("apartment");
export const Building2 = glyph("domain");
export const Store = glyph("storefront");
/** Dar de alta una empresa — el badge "primer paso" del onboarding. */
export const DomainAdd = glyph("domain_add");
export const Network = glyph("account_tree");
export const Layers = glyph("layers");
export const Boxes = glyph("inventory_2");
export const Tag = glyph("label");
export const Target = glyph("track_changes");
export const BarChart3 = glyph("bar_chart");
export const LayoutGrid = glyph("dashboard");
export const Truck = glyph("local_shipping");
export const Wrench = glyph("build");
export const CreditCard = glyph("credit_card");
export const ShoppingCart = glyph("shopping_cart");

/* --- contacto y dispositivos ----------------------------------------------- */
export const Mail = glyph("mail");
/** "Ya te mandamos el correo, andá a buscarlo" — el acuse del envío, no la
 * casilla ni el mensaje. Lo usa el paso final de recuperar contraseña. */
export const MailRead = glyph("mark_email_read");
/** Casilla de correo como concepto (el bloque "Contacto"), no un mensaje. */
export const AtSign = glyph("alternate_email");
export const Phone = glyph("call");
export const Bell = glyph("notifications");
/** Aviso que ya está sonando — el buzón de vencimientos. `Bell` es el mudo. */
export const BellRing = glyph("notifications_active");
export const MapPin = glyph("location_on");
export const Smartphone = glyph("smartphone");
export const SmartphoneNfc = glyph("phonelink_ring");
export const Monitor = glyph("desktop_windows");
export const Headset = glyph("headset_mic");
export const LifeBuoy = glyph("support");

/* --- fechas ----------------------------------------------------------------- */
export const Calendar = glyph("calendar_today");
export const CalendarClock = glyph("event_upcoming");
export const CalendarRange = glyph("date_range");
